import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { UserPlus } from 'lucide-react'
import { addMakeupVisitor, fetchUnrepairedAbsences } from '../api/attendance'
import { searchDancers, type DancerHit } from '../api/dancers'
/**
 * Adding a dancer to this one night, from a teacher's own screen (D-632).
 *
 * **The same door the wall tablet has always had**, and deliberately the same
 * endpoint: a visitor row on this occurrence. Named against an absence it
 * repairs that absence; named against nothing it becomes a banked credit the
 * moment she is marked present, which is what the pilot asked for — *"add a
 * kid to a single instance of a class and it would then account towards that
 * class's banked time"*.
 *
 * **It never creates an enrolment** (D-111, D-264). Nothing in this app does,
 * because Jackrabbit owns billing and there is no write path back; a visitor
 * on one night is a different fact from being in the class, and it is the one
 * this control writes.
 *
 * **The studio's search, not the kiosk's.** The wall screen has its own
 * narrow lookup because it is anonymous (D-233); a signed-in teacher holds
 * `ViewPeople` already — the roster she is looking at is behind it.
 */
export default function AddToNight({ occurrenceId, onAdded }: { occurrenceId: string; onAdded: () => void }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<DancerHit | null>(null)

  const hits = useQuery({
    queryKey: ['dancer-search', query],
    queryFn: () => searchDancers(query),
    enabled: open && query.trim().length >= 2,
  })

  /*
   * What she might be here to make up. Offered, never demanded — the kiosk
   * learned that refusing her at the door sends a child home, and a credit
   * with no absence named is a perfectly good outcome (D-610).
   */
  /*
   * Keyed on the night as well as the dancer (D-795). The list is now specific
   * to what is taught in this room, so caching it under the dancer alone would
   * serve her ballet night's answer on a jazz one — the exact fault this
   * change exists to close, rebuilt in the cache.
   */
  const absences = useQuery({
    queryKey: ['absences', picked?.dancerId, occurrenceId],
    queryFn: () => fetchUnrepairedAbsences(picked!.dancerId, occurrenceId),
    enabled: picked !== null,
  })

  const add = useMutation({
    mutationFn: (missedOccurrenceId: string | null) =>
      addMakeupVisitor(occurrenceId, picked!.dancerId, missedOccurrenceId),
    onSuccess: () => {
      onAdded()
      setOpen(false)
      setPicked(null)
      setQuery('')
    },
  })

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-5 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-dashed border-line-2 py-3 text-[13.5px] font-semibold text-ink-3"
      >
        <UserPlus size={15} strokeWidth={2} aria-hidden="true" />
        Add a dancer to this night
      </button>
    )
  }

  return (
    <div className="mt-5 rounded-xl border border-line bg-surface-2 p-3">
      {picked === null ? (
        <>
          <label className="block text-[12.5px] font-semibold" htmlFor="add-to-night">
            Who is here?
          </label>

          <input
            id="add-to-night"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a name"
            autoComplete="off"
            className="mt-1 min-h-[44px] w-full rounded-lg border border-line bg-surface px-3 text-[14px]"
          />

          {hits.data && hits.data.length > 0 && (
            <ul className="mt-2 flex flex-col gap-1">
              {hits.data.map((hit) => (
                <li key={hit.dancerId}>
                  <button
                    type="button"
                    onClick={() => setPicked(hit)}
                    className="flex min-h-[44px] w-full flex-col justify-center rounded-lg px-3 text-left text-[14px] hover:bg-surface"
                  >
                    <span className="font-semibold wrap-anywhere">{hit.name}</span>
                    {/*
                      Her age, because the household repeats her surname and
                      tells a teacher nothing: searching "Cotton" offered three
                      lines each reading "Cotton" underneath. Age is what picks
                      one child out of a family, and it is what the kiosk shows.
                    */}
                    <span className="text-[12px] text-ink-3 wrap-anywhere">
                      {hit.ageYears ? `Age ${hit.ageYears}` : 'Age not on file'}
                      {hit.household && ` · ${hit.household}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Never a statement about the studio while nothing has been asked (D-209). */}
          {query.trim().length >= 2 && hits.isSuccess && hits.data.length === 0 && (
            <p className="mt-2 text-[12.5px] text-ink-3">Nobody of that name.</p>
          )}

          {hits.isError && (
            <p role="alert" className="mt-2 text-[12.5px] text-crit">
              That search did not work. Try again in a moment.
            </p>
          )}
        </>
      ) : (
        <>
          <p className="text-[14px] font-semibold wrap-anywhere">{picked.name}</p>

          {/*
            <b>Her name, or nobody's pronoun</b> (D-807).
            `asked`, 2026-09-23, with a screenshot of *"Which class is she
            making up?"* above a boy's name: *"we have another spot where we
            are assuming the dancer is a girl."*

            <b>The name is already on the line above</b>, so using it here
            costs nothing and is warmer than *the dancer*; `them` covers the
            places a name would read awkwardly. The studio is mostly girls,
            which is exactly why this kind of copy gets written and never
            noticed — until it is standing in front of Patrick.
          */}
          <p className="mt-0.5 text-[12.5px] text-ink-2">
            Which class is {firstName(picked.name)} making up?
          </p>

          <div className="mt-2 flex flex-col gap-1.5">
            {(absences.data ?? []).map((absence) => (
              <button
                key={absence.occurrenceId}
                type="button"
                disabled={add.isPending}
                onClick={() => add.mutate(absence.occurrenceId)}
                className="min-h-[44px] rounded-lg border border-line bg-surface px-3 py-2 text-left text-[13.5px] wrap-anywhere"
              >
                {absence.label}
              </button>
            ))}

            <p className="mt-1 text-[12.5px] text-ink-3">
              {absences.data && absences.data.length > 0
                ? 'Or add them without naming one — it banks for a class they miss later.'
                : `Nothing on record that ${firstName(picked.name)} has missed. ` +
                  'Adding them banks it for a class they miss later.'}
            </p>

            <button
              type="button"
              disabled={add.isPending}
              onClick={() => add.mutate(null)}
              className="min-h-[44px] rounded-lg bg-accent px-3 text-[13.5px] font-semibold text-on-accent disabled:opacity-50"
            >
              {absences.data && absences.data.length > 0
                ? 'Not sure — add them anyway'
                : `Add ${firstName(picked.name)}`}
            </button>
          </div>
        </>
      )}

      {add.isError && (
        <p role="alert" className="mt-2 text-[12.5px] text-crit">
          {(add.error as Error).message}
        </p>
      )}

      <button
        type="button"
        onClick={() => {
          setOpen(false)
          setPicked(null)
          setQuery('')
        }}
        className="mt-2 min-h-[44px] w-full text-[12.5px] font-semibold text-ink-3 underline underline-offset-2"
      >
        Cancel
      </button>
    </div>
  )
}

/**
 * The name a sentence can use without guessing (D-807).
 *
 * **Not a pronoun.** The studio is overwhelmingly girls, which is exactly why
 * copy like *"which class is she making up?"* gets written and never noticed —
 * until it is on screen above a boy's name, which is how the owner found it.
 * A name is warmer than *the dancer* and cannot be wrong; `them` covers the
 * places a name would read badly.
 *
 * Falls back to the whole string, because a single-word name is somebody's
 * whole name rather than a mistake.
 */
export function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] || full
}
